// Seitenweise geladene Liste (Kontakte, Aktivitäten): erste Seite beim Wechsel der Parameter, weitere Seiten beim Scrollen,
// veraltete Antworten werden verworfen. `schluessel` ändert sich, sobald sich Filter, Suche oder Sortierung ändern.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Liste } from "../../api/types";

export interface ListenZustand<T> {
  zeilen: T[];
  total: number | null;
  hatMehr: boolean;
  laedt: boolean;
  laedtMehr: boolean;
  fehler: unknown;
  /** erste Seite neu holen (zeigt die alte Liste weiter, bis die neue da ist, wenn `still`) */
  neuLaden: (still?: boolean) => void;
  mehr: () => void;
}

export function useListe<T extends { id: string }>(
  holen: (cursor: string | null, signal: AbortSignal) => Promise<Liste<T>>,
  schluessel: string,
): ListenZustand<T> {
  const [zeilen, setZeilen] = useState<T[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const generation = useRef(0);
  const abbruch = useRef<AbortController | null>(null);
  const holenRef = useRef(holen);
  holenRef.current = holen;

  const neuLaden = useCallback((still = false) => {
    const gen = ++generation.current;
    abbruch.current?.abort();
    const ctrl = new AbortController();
    abbruch.current = ctrl;
    if (!still) {
      setLaedt(true);
      setZeilen([]);
      setTotal(null);
      setCursor(null);
    }
    setLaedtMehr(false);
    setFehler(null);
    holenRef
      .current(null, ctrl.signal)
      .then((r) => {
        if (gen !== generation.current) return;
        setZeilen(r.data);
        setTotal(r.total);
        setCursor(r.has_more ? r.cursor : null);
        setLaedt(false);
      })
      .catch((e) => {
        if (gen !== generation.current || (e as Error).name === "AbortError") return;
        setFehler(e);
        setLaedt(false);
      });
  }, []);

  useEffect(() => {
    neuLaden(false);
    return () => {
      generation.current++;
      abbruch.current?.abort();
    };
  }, [schluessel, neuLaden]);

  const mehr = useCallback(() => {
    if (!cursor || laedtMehr || laedt) return;
    const gen = generation.current;
    const ctrl = abbruch.current ?? new AbortController();
    setLaedtMehr(true);
    holenRef
      .current(cursor, ctrl.signal)
      .then((r) => {
        if (gen !== generation.current) return;
        setZeilen((z) => {
          const bekannt = new Set(z.map((x) => x.id));
          return [...z, ...r.data.filter((x) => !bekannt.has(x.id))];
        });
        setCursor(r.has_more ? r.cursor : null);
        setLaedtMehr(false);
      })
      .catch((e) => {
        if (gen !== generation.current || (e as Error).name === "AbortError") return;
        setFehler(e);
        setLaedtMehr(false);
      });
  }, [cursor, laedtMehr, laedt]);

  return { zeilen, total, hatMehr: !!cursor, laedt, laedtMehr, fehler, neuLaden, mehr };
}
